/**
 * Real Card Offers Service
 * Fetches current credit card offers and promotions using free sources
 */

import { LoyaltyProgram } from '../types';

export interface RealCardOffer {
  id: string;
  bank: string;
  cardName: string;
  offer: string;
  bonusPoints: number;
  requirement: string;
  spendingRequirement: number; // in dollars
  timeLimit: string;
  estimatedValue: number; // in rupees
  category: string;
  annualFee: number;
  currentAPR: string;
  isLimitedTime: boolean;
  expiryDate?: Date;
  source: 'bank_website' | 'affiliate' | 'promotion';
  lastUpdated: Date;
}

export interface PersonalizedOffer {
  offer: RealCardOffer;
  matchScore: number; // 0-100 based on user's existing cards and spending
  reasonToApply: string;
  potentialValue: number;
  conflicts: string[]; // existing cards from same issuer
}

class RealCardOffersService {
  private cachedOffers: RealCardOffer[] = [];
  private lastFetchTime: Date | null = null;
  private readonly CACHE_DURATION = 1000 * 60 * 60 * 6; // 6 hours

  /**
   * Get current real card offers from multiple sources
   */
  async getCurrentOffers(): Promise<RealCardOffer[]> {
    // Check cache first
    if (this.cachedOffers.length > 0 && this.lastFetchTime) {
      const timeSinceLastFetch = Date.now() - this.lastFetchTime.getTime();
      if (timeSinceLastFetch < this.CACHE_DURATION) {
        console.log('🎯 Returning cached card offers');
        return this.cachedOffers;
      }
    }

    console.log('🔄 Fetching real card offers...');
    
    try {
      // In a real implementation, this would fetch from:
      // 1. Bank APIs (Chase, Amex, etc.)
      // 2. Affiliate networks (Commission Junction, etc.)
      // 3. Web scraping (NerdWallet, etc.)
      // 4. RSS feeds from credit card blogs
      
      const offers = await this.fetchOffersFromSources();
      this.cachedOffers = offers;
      this.lastFetchTime = new Date();
      
      return offers;
    } catch (error) {
      console.warn('Could not fetch real offers, returning fallback data:', error);
      return this.getFallbackOffers();
    }
  }

  /**
   * Get personalized offers based on user's existing cards and spending
   */
  async getPersonalizedOffers(
    userCards: LoyaltyProgram[],
    monthlySpending: number = 3000
  ): Promise<PersonalizedOffer[]> {
    const allOffers = await this.getCurrentOffers();
    const personalizedOffers: PersonalizedOffer[] = [];

    for (const offer of allOffers) {
      const analysis = this.analyzeOfferForUser(offer, userCards, monthlySpending);
      if (analysis.matchScore > 30) { // Only show relevant offers
        personalizedOffers.push(analysis);
      }
    }

    // Sort by match score (highest first)
    return personalizedOffers.sort((a, b) => b.matchScore - a.matchScore);
  }

  /**
   * Analyze how relevant an offer is for a specific user
   */
  private analyzeOfferForUser(
    offer: RealCardOffer,
    userCards: LoyaltyProgram[],
    monthlySpending: number
  ): PersonalizedOffer {
    let matchScore = 50; // Base score
    let reasonToApply = '';
    let potentialValue = offer.estimatedValue;
    const conflicts: string[] = [];

    // Check for existing cards from same bank
    const existingFromBank = userCards.filter(card => 
      card.name.toLowerCase().includes(offer.bank.toLowerCase())
    );

    if (existingFromBank.length > 0) {
      matchScore -= 20; // Reduce score for same bank
      conflicts.push(`You already have ${existingFromBank.length} card(s) from ${offer.bank}`);
    }

    // Analyze spending requirement feasibility
    const monthsToMeetRequirement = offer.spendingRequirement / monthlySpending;
    if (monthsToMeetRequirement <= 3) {
      matchScore += 20;
      reasonToApply = `Easy to meet spending requirement with your ₹${(monthlySpending * 88.62).toLocaleString()}/month budget`;
    } else if (monthsToMeetRequirement <= 6) {
      matchScore += 10;
      reasonToApply = `Achievable spending requirement over ${Math.ceil(monthsToMeetRequirement)} months`;
    } else {
      matchScore -= 15;
      reasonToApply = `High spending requirement may be difficult to meet`;
    }

    // Bonus for limited time offers
    if (offer.isLimitedTime) {
      matchScore += 10;
      reasonToApply += '. Limited time offer!';
    }

    // Calculate actual value based on user's spending
    const timeToEarnBonus = monthsToMeetRequirement;
    if (timeToEarnBonus <= 4) {
      potentialValue = offer.bonusPoints * 0.01; // Estimate 1¢ per point value
    }

    return {
      offer,
      matchScore: Math.max(0, Math.min(100, matchScore)),
      reasonToApply: reasonToApply || 'Good rewards program',
      potentialValue,
      conflicts
    };
  }

  /**
   * Fetch offers from real sources (placeholder for actual implementation)
   */
  private async fetchOffersFromSources(): Promise<RealCardOffer[]> {
    // This would be replaced with actual API calls or web scraping
    // For now, return enhanced realistic data based on current market offers
    
    await new Promise(resolve => setTimeout(resolve, 1000)); // Simulate API call

    return [
      {
        id: 'hdfc-infinia-2024',
        bank: 'HDFC Bank',
        cardName: 'Infinia Credit Card',
        offer: '10,000 reward points',
        bonusPoints: 10000,
        requirement: 'Spend ₹3,30,000 in first 3 months',
        spendingRequirement: 4000,
        timeLimit: '3 months',
        estimatedValue: 1000, // ₹1000 value from 10,000 points at 0.1 per point
        category: 'Travel & Dining',
        annualFee: 12500,
        currentAPR: '3.5% monthly',
        isLimitedTime: true,
        expiryDate: new Date('2025-03-31'),
        source: 'bank_website',
        lastUpdated: new Date()
      },
      {
        id: 'axis-magnus-2024',
        bank: 'Axis Bank',
        cardName: 'Magnus Credit Card',
        offer: '25,000 edge miles',
        bonusPoints: 25000,
        requirement: 'Spend ₹1,00,000 in first 90 days',
        spendingRequirement: 1200,
        timeLimit: '90 days',
        estimatedValue: 2250, // ₹2250 value from 25,000 miles at 0.09 per mile
        category: 'Travel & Dining',
        annualFee: 12500,
        currentAPR: '3.5% monthly',
        isLimitedTime: false,
        source: 'bank_website',
        lastUpdated: new Date()
      },
      {
        id: 'sbi-cashback-2024',
        bank: 'SBI Card',
        cardName: 'Cashback Credit Card',
        offer: '₹2,000 cashback',
        bonusPoints: 2000,
        requirement: 'Spend ₹15,000 in first 60 days',
        spendingRequirement: 180,
        timeLimit: '60 days',
        estimatedValue: 2000,
        category: 'Online Shopping',
        annualFee: 0,
        currentAPR: '3.5% monthly',
        isLimitedTime: false,
        source: 'bank_website',
        lastUpdated: new Date()
      },
      {
        id: 'icici-amazon-2024',
        bank: 'ICICI Bank',
        cardName: 'Amazon Pay Credit Card',
        offer: '₹2,500 Amazon Pay Gift Card',
        bonusPoints: 2500,
        requirement: 'Spend ₹15,000 in first 60 days',
        spendingRequirement: 180,
        timeLimit: '60 days',
        estimatedValue: 2500,
        category: 'Online Shopping',
        annualFee: 0,
        currentAPR: '3.35% monthly',
        isLimitedTime: true,
        expiryDate: new Date('2025-03-31'),
        source: 'affiliate',
        lastUpdated: new Date()
      }
    ];
  }

  /**
   * Fallback offers when real data isn't available
   */
  private getFallbackOffers(): RealCardOffer[] {
    return [
      {
        id: 'fallback-1',
        bank: 'IndusInd Bank',
        cardName: 'Legend Credit Card',
        offer: '50,000 reward points',
        bonusPoints: 50000,
        requirement: 'Spend ₹2,50,000 in 3 months',
        spendingRequirement: 3000,
        timeLimit: '3 months',
        estimatedValue: 5000, // ₹5000 value from 50,000 points at 0.1 per point
        category: 'Travel',
        annualFee: 7500,
        currentAPR: '19.99% - 26.99%',
        isLimitedTime: false,
        source: 'bank_website',
        lastUpdated: new Date()
      }
    ];
  }

  /**
   * Get trending offers with high value
   */
  async getTrendingOffers(): Promise<RealCardOffer[]> {
    const allOffers = await this.getCurrentOffers();
    
    return allOffers
      .filter(offer => offer.estimatedValue >= 400) // High value offers
      .sort((a, b) => b.estimatedValue - a.estimatedValue)
      .slice(0, 6); // Top 6 offers
  }

  /**
   * Get offers suitable for beginners
   */
  async getBeginnerOffers(): Promise<RealCardOffer[]> {
    const allOffers = await this.getCurrentOffers();
    
    return allOffers
      .filter(offer => 
        offer.annualFee === 0 && // No annual fee
        offer.spendingRequirement <= 2000 // Low spending requirement
      )
      .sort((a, b) => a.spendingRequirement - b.spendingRequirement);
  }

  /**
   * Clear cache to force fresh data
   */
  clearCache(): void {
    this.cachedOffers = [];
    this.lastFetchTime = null;
  }
}

export default new RealCardOffersService();